// Client-side source / sub-type filter toggles (MOCK-DATA.md §4).
//
// Filtering operates on already-fetched data — that is what makes the chips
// feel instant. Six toggles in two groups: the Sales Center group (plain events
// plus three sub-types) and the PestPac / Google sources. The `isVisible`
// predicate is exported so it is unit-tested without rendering.

import { useCallback, useState } from 'react'
import type { CalendarEvent, CalendarSubType } from '../typings'

/** `sales_center` governs Sales Center events with no sub-type (plain calendar events). */
export type FilterKey = CalendarSubType | 'sales_center' | 'pestpac' | 'google'

export type EventFilters = Record<FilterKey, boolean>

export const DEFAULT_FILTERS: EventFilters = {
  sales_center: true,
  activity_reminder: true,
  next_action_date: true,
  expected_close_date: true,
  pestpac: true,
  google: true,
}

/**
 * Whether an event passes the current filters.
 *  - Sales Center WITH a sub-type: governed by that sub-type's chip.
 *  - Sales Center WITHOUT a sub-type: governed by the "Events" chip.
 *  - PestPac / Google: governed by their source chip.
 *  - Unknown source: no chip → always visible (never silently hidden).
 */
export function isVisible(
  event: CalendarEvent,
  filters: EventFilters,
): boolean {
  if (event.source === 'sales_center') {
    // activity_reminder is retired as a user-facing toggle (SH-2102): the
    // Activities chip now opens useActivityTypeFilter's menu instead, which
    // governs these events on its own. Bypassing here avoids stranding a
    // pre-SH-2102 `activity_reminder: false` preference with no UI left to
    // undo it.
    if (!event.subType || event.subType === 'activity_reminder') return true
    return filters[event.subType]
  }
  if (event.source === 'pestpac') {
    return filters.pestpac
  }
  if (event.source === 'google') {
    return filters.google
  }
  return true
}

export type UseEventFiltersResult = {
  filters: EventFilters
  toggle: (key: FilterKey) => void
  visible: (event: CalendarEvent) => boolean
}

export function useEventFilters(
  initial: EventFilters = DEFAULT_FILTERS,
): UseEventFiltersResult {
  const [filters, setFilters] = useState<EventFilters>(initial)

  const toggle = useCallback((key: FilterKey) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const visible = useCallback(
    (event: CalendarEvent) => isVisible(event, filters),
    [filters],
  )

  return { filters, toggle, visible }
}
